import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, Profile } from '../lib/supabase';
import {
  ImpersonationState,
  clearImpersonationState,
  readImpersonationState,
  writeImpersonationState,
} from '../utils/impersonation';
import { API_BASE_URL } from '../utils/apiUrl';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  impersonation: ImpersonationState | null;
  loading: boolean;
  sessionError: boolean;
  signUp: (email: string, password: string, fullName: string, companyName?: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  startImpersonation: (options: {
    accessToken: string;
    refreshToken: string;
    targetUserId: string;
    targetEmail: string;
    targetName?: string;
    adminReturnPath?: string;
  }) => Promise<void>;
  exitImpersonation: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
  refreshSession: () => Promise<void>;
  clearSessionError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(() => readImpersonationState());
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState(false);
  const activeUserIdRef = useRef<string | null>(null);

  // Fetch user profile from database
  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching profile:', error);
        return null;
      }

      if (activeUserIdRef.current === userId) {
        setProfile(data);
      }

      return data;
    } catch (error) {
      console.error('Unexpected error fetching profile:', error);
      return null;
    }
  };

  // Initialize auth state
  useEffect(() => {
    let isMounted = true;

    const syncImpersonation = (nextUser: User | null) => {
      const storedImpersonation = readImpersonationState();

      if (!storedImpersonation) {
        setImpersonation(null);
        return;
      }

      if (!nextUser) {
        setImpersonation(storedImpersonation);
        return;
      }

      if (nextUser.id === storedImpersonation.targetUserId) {
        setImpersonation(storedImpersonation);
        return;
      }

      if (nextUser.id === storedImpersonation.adminUserId) {
        clearImpersonationState();
        setImpersonation(null);
        return;
      }

      clearImpersonationState();
      setImpersonation(null);
    };

    const applyAuthState = (nextSession: Session | null) => {
      const nextUser = nextSession?.user ?? null;

      activeUserIdRef.current = nextUser?.id ?? null;
      setSession(nextSession);
      setUser(nextUser);
      setSessionError(false);
      syncImpersonation(nextUser);

      if (!nextUser) {
        setProfile(null);
      }
    };

    const loadProfile = (userId: string) => {
      window.setTimeout(() => {
        if (!isMounted || activeUserIdRef.current !== userId) {
          return;
        }

        void fetchProfile(userId);
      }, 0);
    };
    
    // Get initial session
    const initializeAuth = async () => {
      try {
        const { data: { session: initialSession }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting session:', error);
          if (isMounted) {
            setLoading(false);
          }
          return;
        }

        if (isMounted) {
          applyAuthState(initialSession);

          if (initialSession?.user) {
            console.log('Found existing session for user:', initialSession.user.id);
            loadProfile(initialSession.user.id);
          } else {
            console.log('No existing session found');
          }
          
          setLoading(false);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        console.log('Auth state change:', event);
        
        if (!isMounted) return;
        
        // Handle different auth events
        switch (event) {
          case 'SIGNED_OUT':
            clearImpersonationState();
            setImpersonation(null);
            applyAuthState(null);
            // Redirect to login page
            window.location.href = '/login';
            break;
            
          case 'TOKEN_REFRESHED':
          case 'SIGNED_IN':
            applyAuthState(newSession);

            if (newSession?.user) {
              loadProfile(newSession.user.id);
            }

            // Force a window event to notify components
            window.dispatchEvent(new Event('auth-refresh'));
            break;
            
          case 'USER_UPDATED':
            if (newSession?.user) {
              applyAuthState(newSession);
              loadProfile(newSession.user.id);
            }
            break;
            
          default:
            applyAuthState(newSession);

            if (newSession?.user) {
              loadProfile(newSession.user.id);
            }
        }
        
        setLoading(false);
      }
    );

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, fullName: string, companyName?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          company_name: companyName,
        },
      },
    });
    
    if (error) throw error;
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ 
      email, 
      password 
    });
    
    if (error) throw error;
  };

  const resetPassword = async (email: string) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to send reset email');
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Clear all local storage
      activeUserIdRef.current = null;
      setProfile(null);
      setImpersonation(null);
      clearImpersonationState();
      localStorage.clear();
      sessionStorage.clear();
      
      // Navigate to login
      window.location.href = '/login';
    } catch (error) {
      console.error('Sign out error:', error);
      // Force navigation even if signout fails
      window.location.href = '/login';
    }
  };

  const startImpersonation = useCallback(async (options: {
    accessToken: string;
    refreshToken: string;
    targetUserId: string;
    targetEmail: string;
    targetName?: string;
    adminReturnPath?: string;
  }) => {
    const { data: { session: adminSession }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError || !adminSession?.user || !adminSession.access_token || !adminSession.refresh_token) {
      throw sessionError || new Error('Missing current admin session');
    }

    const nextImpersonationState: ImpersonationState = {
      adminUserId: adminSession.user.id,
      adminEmail: adminSession.user.email || '',
      adminSession: {
        accessToken: adminSession.access_token,
        refreshToken: adminSession.refresh_token,
      },
      adminReturnPath: options.adminReturnPath || '/admin/users',
      targetUserId: options.targetUserId,
      targetEmail: options.targetEmail,
      targetName: options.targetName,
      startedAt: new Date().toISOString(),
    };

    writeImpersonationState(nextImpersonationState);
    setImpersonation(nextImpersonationState);

    const { error: impersonationError } = await supabase.auth.setSession({
      access_token: options.accessToken,
      refresh_token: options.refreshToken,
    });

    if (impersonationError) {
      clearImpersonationState();
      setImpersonation(null);
      throw impersonationError;
    }

    const { data: userData, error: userError } = await supabase.auth.getUser(options.accessToken);

    if (userError || !userData.user) {
      clearImpersonationState();
      setImpersonation(null);
      throw userError || new Error('Failed to verify impersonated session');
    }
  }, []);

  const exitImpersonation = useCallback(async () => {
    const storedImpersonation = readImpersonationState();

    if (!storedImpersonation?.adminSession.accessToken || !storedImpersonation.adminSession.refreshToken) {
      clearImpersonationState();
      setImpersonation(null);
      throw new Error('Admin session backup is not available');
    }

    const { error } = await supabase.auth.setSession({
      access_token: storedImpersonation.adminSession.accessToken,
      refresh_token: storedImpersonation.adminSession.refreshToken,
    });

    if (error) {
      throw error;
    }

    clearImpersonationState();
    setImpersonation(null);
  }, []);
  
  const refreshSession = useCallback(async () => {
    try {
      const { data: { session: newSession }, error } = await supabase.auth.refreshSession();
      if (error || !newSession) {
        setSessionError(true);
        throw error || new Error('Failed to refresh session');
      }
      
      setSession(newSession);
      setUser(newSession.user);
      activeUserIdRef.current = newSession.user.id;
      setSessionError(false);
      
      // Dispatch event to notify components
      window.dispatchEvent(new Event('auth-refresh'));
      
      if (newSession.user) {
        await fetchProfile(newSession.user.id);
      }
    } catch (error) {
      console.error('Session refresh error:', error);
      setSessionError(true);
      // Redirect to login on refresh failure
      window.location.href = '/login';
    }
  }, []);
  
  const clearSessionError = useCallback(() => {
    setSessionError(false);
  }, []);

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) throw new Error('No user logged in');

    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id);

    if (error) throw error;
    
    // Refresh profile
    await fetchProfile(user.id);
  };

  const value = {
    session,
    user,
    profile,
    impersonation,
    loading,
    sessionError,
    signUp,
    signIn,
    signOut,
    resetPassword,
    startImpersonation,
    exitImpersonation,
    updateProfile,
    refreshSession,
    clearSessionError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
